import { Outlet, useLocation, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { getProject, getProjectStages, getSectionSubsections, getStageSections } from "../../Services/api";
import LoaderComponent from "../../Components/LoaderComponent/LoaderComponent";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import ProjectHeaderComponent from "../../Components/ProjectHeaderComponent/ProjectHeaderComponent";
import { motion, Variants } from "framer-motion";
import GanttChart from "../../Components/GanttComponent/GanttChart";
import Button from "@mui/material/Button";

const variants: Variants = {
  open: {
    height: "auto",
    opacity: 1,
    transition: { duration: 0.5, ease: "easeInOut" },
  },
  collapsed: {
    height: 0,
    opacity: 0,
    transition: { duration: 0.5, ease: "easeInOut" },
  },
};

export default function ProjectWrapperPage() {
  const { id } = useParams();
  const { state } = useLocation();

  const [project, setProject] = useState<any>([]);
  const [stages, setStages] = useState<any>([]);

  const [hideDelays, setHideDelays] = useState(false);
  const [hideLegend, setHideLegend] = useState(true);

  const [isGantView, setIsGantView] = useState(false);
  const [isGantLoading, setIsGantLoading] = useState(false);
  const [currentGanttDataType, setCurrentGanttDataType] = useState<"stages" | "sections" | "subSections">("stages");
  const [ganttData, setGanttData] = useState<any>([]);
  const [ganttBackData, setGanttBackData] = useState<any>(null);
  const [ganttStageData, setGanttStageData] = useState<any>(0);
  const [ganttSectionData, setGanttSectionData] = useState<any>(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [breadcrumbs, setBreadcrumbs] = React.useState<any[]>([]);
  const [backButton, setBackButton] = React.useState<any>({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    getProjectStages(id).then((stagesData) => {
      setStages(stagesData.stages);
      setGanttData(stagesData.stages);

      if (state && state.project) {
        setProject(state.project);
        setLoading(false);
      } else {
        getProject(id)
          .then((projectData) => {
            setProject(projectData);
            setLoading(false);
          })
          .catch((err) => {
            setError(err.message || "Unknown error");
          })
          .finally(() => {});
      }
    });

    const resetFocus = document.getElementById("reset-focus");
    if (resetFocus) resetFocus.focus();
  }, [state, id]);

  const onChangeGanttData = (data: any) => {
    if (data.type) {
      // When go back is clicked :
      if (data.type === "stage") {
        setGanttData(stages);
        setGanttBackData(null);
        setCurrentGanttDataType("stages");
      } else if (data.type === "section") {
        setIsGantLoading(true);
        getStageSections(id, data.id).then((sectionsData) => {
          setGanttData(sectionsData.sections);
          setIsGantLoading(false);
          setGanttBackData({ id: data.id, name: data.parentName, type: "stage" });
          setCurrentGanttDataType("sections");
        });
      }
    } else {
      // When regular row is clicked :
      if (currentGanttDataType === "stages") {
        setIsGantLoading(true);
        setGanttBackData({ id: data.id, name: data.name, type: "stage" });
        getStageSections(id, data.id).then((data) => {
          setGanttData(data.sections);
          setIsGantLoading(false);
        });
        setGanttStageData(data);
        setCurrentGanttDataType("sections");
      } else if (currentGanttDataType === "sections") {
        setIsGantLoading(true);
        setGanttBackData({ id: ganttBackData.id, name: data.name, parentName: ganttBackData.name, type: "section" });
        getSectionSubsections(id, ganttStageData.id, data.id).then((data) => {
          setGanttData(data.subsections);
          setIsGantLoading(false);
        });
        setGanttSectionData(data);
        setCurrentGanttDataType("subSections");
      }
    }
  };

  if (loading) return <LoaderComponent />;
  if (error) return <p>Error: {error}</p>;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        bgcolor: "#e4eeff",
        justifyContent: "space-between",
        paddingInline: { sm: "30px" },
      }}
    >
      <ProjectHeaderComponent id={id} project={project} breadcrumbs={breadcrumbs} backButton={backButton} />
      <motion.div
        key={window.location.pathname}
        variants={variants}
        initial="collapsed"
        animate="open"
        exit="collapsed"
        style={{ overflow: "hidden" }}
      >
        <Box id={"content"}>
          <Box
            sx={{
              maxWidth: "100%",
              minHeight: "615px",
              p: { sm: "30px" },
              borderRadius: "26px",
              bgcolor: "white",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              mb: "15px",
            }}
          >
            {!isMobile && (
              <Box display={"flex"} justifyContent={"end"}>
                <Box className="gant-controls" sx={{ p: "30px" }}>
                  <button
                    className={hideDelays ? "delay-button" : "hide-delay-button"}
                    style={{ fontWeight: "bold" }}
                    onClick={() => setHideDelays(!hideDelays)}
                  >
                    {hideDelays ? "הצג עיכובים" : "הסתר עיכובים"}
                  </button>
                  <img
                    tabIndex={0}
                    src={isGantView ? "/images/stepper_icon.png" : "/images/stepper_icon_active.png"}
                    onClick={() => setIsGantView(false)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setIsGantView(false);
                    }}
                    className="gant-icon"
                    alt="Speeder"
                    aria-label="תצוגת זמנים"
                    role="link"
                  />
                  <img
                    tabIndex={0}
                    src={isGantView ? "/images/gant_icon_active.png" : "/images/gant_icon.png"}
                    onClick={() => setIsGantView(true)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") setIsGantView(true);
                    }}
                    className="gant-icon"
                    alt="Task"
                    aria-label="תצוגת גרף"
                    role="link"
                  />
                </Box>
              </Box>
            )}

            {isMobile && (
              <Box display={"flex"} justifyContent={"space-between"} sx={{ p: "30px" }}>
                <Button
                  variant={"text"}
                  className={hideLegend ? "delay-button" : "hide-delay-button"}
                  style={{ fontWeight: "bold" }}
                  onClick={() => setHideLegend(!hideLegend)}
                >
                  {hideLegend && <img style={{ marginLeft: 10 }} src="/images/legend-icon-closed.png" />}
                  {!hideLegend && <img style={{ marginLeft: 10 }} src="/images/legend-icon-opened.png" />}
                  {hideLegend ? "לפתיחת המקרא" : "סגירת המקרא"}
                </Button>

                <button
                  className={hideDelays ? "delay-button" : "hide-delay-button"}
                  style={{ fontWeight: "bold" }}
                  onClick={() => setHideDelays(!hideDelays)}
                >
                  {hideDelays ? "הצג עיכובים" : "הסתר עיכובים"}
                </button>
              </Box>
            )}

            {isGantView ? (
              <GanttChart
                stages={ganttData}
                backData={ganttBackData}
                loading={isGantLoading}
                hideDelays={hideDelays}
                onStageClick={onChangeGanttData}
              />
            ) : (
              <Outlet context={{ hideDelays, hideLegend, stages, setBreadcrumbs, setBackButton }} />
            )}
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
}
